import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Typography } from '@mui/joy';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { useNavigate } from 'react-router-dom';
import axiosConfig from '../util/axiosConfig';
import { formatDateTime } from '../util/dateFormat';
import { STUDENT_NOTIFICATION_RECEIVED } from '../util/studentNotificationEvents';

const messageCellTextSx = {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    overflowWrap: 'break-word'
};

const extractQuizCodeFromNotification = (notification) => {
    const data = notification?.data && typeof notification.data === 'object' ? notification.data : null;
    const fromData =
        data?.sessionId
        || data?.sessionID
        || data?.session_id
        || data?.code
        || data?.quizCode
        || data?.roomCode;
    if (typeof fromData === 'string' && fromData.trim()) {
        return fromData.trim();
    }

    const text = `${notification?.title || ''}\n${notification?.body || ''}`.trim();
    // Examples we expect: "Room code: MOOU05" / "Quiz code: SW4669"
    const match = text.match(/\b(?:room|quiz)\s*code\s*:\s*([A-Za-z0-9_-]{3,50})\b/i);
    return match?.[1] ? String(match[1]).trim() : null;
};

const UnreadNotificationsPreview = ({ limit = 5 }) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [unreadCount, setUnreadCount] = useState(0);
    const [items, setItems] = useState([]);

    const goToNotifications = () => navigate('/notifications');
    const goToQuiz = (code) => navigate(`/quiz/${encodeURIComponent(code)}`);

    const loadUnread = useCallback(async () => {
        try {
            setError('');
            const res = await axiosConfig.get('/students/notifications', {
                params: {
                    limit,
                    unreadOnly: true
                }
            });
            setItems(Array.isArray(res.data?.notifications) ? res.data.notifications : []);
            setUnreadCount(Number(res.data?.unreadCount || 0));
        } catch (e) {
            setError(e?.response?.data?.errorMessage || e?.message || 'Failed to load unread notifications');
            setItems([]);
            setUnreadCount(0);
        } finally {
            setLoading(false);
        }
    }, [limit]);

    useEffect(() => {
        setLoading(true);
        loadUnread();
    }, [loadUnread]);

    useEffect(() => {
        const onReceived = () => loadUnread();
        window.addEventListener(STUDENT_NOTIFICATION_RECEIVED, onReceived);
        return () => window.removeEventListener(STUDENT_NOTIFICATION_RECEIVED, onReceived);
    }, [loadUnread]);

    if (!loading && unreadCount <= 0) {
        return null;
    }

    return (
        <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent sx={{ p: 3, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <NotificationsActiveIcon sx={{ color: '#eb7a24', fontSize: '1.4rem' }} />
                        <Typography level="title-lg" sx={{ color: '#eb7a24' }}>
                            Unread notifications
                        </Typography>
                        {unreadCount > 0 && (
                            <Chip size="sm" color="warning" variant="solid">
                                {unreadCount}
                            </Chip>
                        )}
                    </Box>
                    <Button
                        size="sm"
                        variant="outlined"
                        color="warning"
                        onClick={goToNotifications}
                        sx={{ borderColor: '#ea7a23', color: '#ea7a23', '&:hover': { bgcolor: '#fff5ed' } }}
                    >
                        View all
                    </Button>
                </Box>

                {error && (
                    <Alert color="danger" variant="soft" sx={{ mb: 1.5 }}>
                        {error}
                    </Alert>
                )}

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress size="sm" />
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {items.slice(0, limit).map((n) => (
                            (() => {
                                const quizCode = extractQuizCodeFromNotification(n);
                                const isLiveQuizInvite = Boolean(quizCode) && (
                                    String(n?.type || '').toLowerCase() === 'quiz'
                                    || String(n?.type || '').toLowerCase() === 'live_quiz'
                                    || String(n?.type || '').toLowerCase() === 'live-quiz'
                                    || /live\s*quiz/i.test(String(n?.title || ''))
                                    || /room\s*code\s*:/i.test(String(n?.body || ''))
                                );
                                const onOpen = () => (isLiveQuizInvite ? goToQuiz(quizCode) : goToNotifications());
                                const aria = isLiveQuizInvite
                                    ? `Join quiz ${quizCode}. ${n.title || 'Notification'}`
                                    : `Open notifications. ${n.title || 'Notification'}`;

                                return (
                            <Box
                                key={n.id}
                                onClick={onOpen}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        onOpen();
                                    }
                                }}
                                tabIndex={0}
                                role="button"
                                aria-label={aria}
                                sx={{
                                    p: 1.5,
                                    borderRadius: 'sm',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    bgcolor: '#fffaf5',
                                    cursor: 'pointer',
                                    '&:hover': { bgcolor: '#fff5ed' }
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
                                    <Typography level="title-sm" sx={{ wordBreak: 'break-word' }}>
                                        {n.title || 'Notification'}
                                    </Typography>
                                    <Typography level="body-xs" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                                        {formatDateTime(n.createdAt)}
                                    </Typography>
                                </Box>
                                {isLiveQuizInvite && (
                                    <Typography level="body-xs" sx={{ mt: 0.25, color: '#ea7a23', fontWeight: 600 }}>
                                        Join: /quiz/{quizCode}
                                    </Typography>
                                )}
                                {n.body ? (
                                    <Typography level="body-sm" sx={{ mt: 0.5, color: 'text.secondary', ...messageCellTextSx }}>
                                        {String(n.body).slice(0, 160)}
                                        {String(n.body).length > 160 ? '…' : ''}
                                    </Typography>
                                ) : null}
                            </Box>
                                );
                            })()
                        ))}
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

export default UnreadNotificationsPreview;
